// src/pinball/CollisionDetector.ts - Spatial grid broad-phase + narrow-phase collision detection

import {
  Vector2D,
  PhysicsBody,
  StaticCollider,
  FlipperBody,
  CollisionResult,
  CollisionPair,
  CircleShape,
  SegmentShape,
  ArcShape,
} from './types';

export class CollisionDetector {
  private gridCellSize: number;
  private grid: Map<string, { bodies: string[]; colliders: string[] }>;
  private bodyMap: Map<string, PhysicsBody>;
  private colliderMap: Map<string, StaticCollider>;

  constructor(gridCellSize: number = 50) {
    this.gridCellSize = gridCellSize;
    this.grid = new Map();
    this.bodyMap = new Map();
    this.colliderMap = new Map();
  }

  private getCellKey(x: number, y: number): string {
    const cx = Math.floor(x / this.gridCellSize);
    const cy = Math.floor(y / this.gridCellSize);
    return `${cx},${cy}`;
  }

  private getCellsForCircle(center: Vector2D, radius: number): string[] {
    const cells: string[] = [];
    const minX = Math.floor((center.x - radius) / this.gridCellSize);
    const maxX = Math.floor((center.x + radius) / this.gridCellSize);
    const minY = Math.floor((center.y - radius) / this.gridCellSize);
    const maxY = Math.floor((center.y + radius) / this.gridCellSize);

    for (let cx = minX; cx <= maxX; cx++) {
      for (let cy = minY; cy <= maxY; cy++) {
        cells.push(`${cx},${cy}`);
      }
    }
    return cells;
  }

  private getCellsForSegment(start: Vector2D, end: Vector2D): string[] {
    const cells = new Set<string>();
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const steps = Math.max(
      Math.abs(dx) / this.gridCellSize,
      Math.abs(dy) / this.gridCellSize,
      1
    );
    const numSteps = Math.ceil(steps) + 1;

    for (let i = 0; i <= numSteps; i++) {
      const t = i / numSteps;
      const x = start.x + dx * t;
      const y = start.y + dy * t;
      cells.add(this.getCellKey(x, y));
    }
    return Array.from(cells);
  }

  updateSpatialGrid(bodies: PhysicsBody[], statics: StaticCollider[]): void {
    this.grid.clear();
    this.bodyMap.clear();
    this.colliderMap.clear();

    // Index bodies
    for (const body of bodies) {
      if (!body.isActive) continue;
      this.bodyMap.set(body.id, body);
      const cells = this.getCellsForCircle(body.position, body.radius);
      for (const cell of cells) {
        if (!this.grid.has(cell)) {
          this.grid.set(cell, { bodies: [], colliders: [] });
        }
        this.grid.get(cell)!.bodies.push(body.id);
      }
    }

    // Index static colliders
    for (const collider of statics) {
      this.colliderMap.set(collider.id, collider);
      let cells: string[];

      switch (collider.type) {
        case 'circle': {
          const shape = collider.shape as CircleShape;
          cells = this.getCellsForCircle(shape.center, shape.radius);
          break;
        }
        case 'segment': {
          const shape = collider.shape as SegmentShape;
          cells = this.getCellsForSegment(shape.start, shape.end);
          break;
        }
        case 'arc': {
          const shape = collider.shape as ArcShape;
          cells = this.getCellsForCircle(shape.center, shape.radius);
          break;
        }
        default:
          cells = [];
      }

      for (const cell of cells) {
        if (!this.grid.has(cell)) {
          this.grid.set(cell, { bodies: [], colliders: [] });
        }
        this.grid.get(cell)!.colliders.push(collider.id);
      }
    }
  }

  getPotentialPairs(): CollisionPair[] {
    const pairs = new Set<string>();
    const result: CollisionPair[] = [];

    for (const [, cell] of this.grid) {
      // Body vs collider
      for (const bodyId of cell.bodies) {
        for (const colliderId of cell.colliders) {
          const key = `${bodyId}:${colliderId}`;
          if (!pairs.has(key)) {
            pairs.add(key);
            result.push({ bodyId, colliderId });
          }
        }
        // Body vs body
        for (const otherBodyId of cell.bodies) {
          if (bodyId >= otherBodyId) continue;
          const key = `${bodyId}:${otherBodyId}`;
          if (!pairs.has(key)) {
            pairs.add(key);
            result.push({ bodyId, colliderId: otherBodyId });
          }
        }
      }
    }

    return result;
  }

  testCircleCircle(a: PhysicsBody, b: PhysicsBody): CollisionResult | null {
    const dx = b.position.x - a.position.x;
    const dy = b.position.y - a.position.y;
    const distSq = dx * dx + dy * dy;
    const minDist = a.radius + b.radius;

    if (distSq >= minDist * minDist) return null;

    const dist = Math.sqrt(distSq);
    if (dist === 0) {
      return {
        normal: { x: 0, y: -1 },
        penetration: minDist,
        contactPoint: { x: a.position.x, y: a.position.y },
      };
    }

    const normal: Vector2D = { x: dx / dist, y: dy / dist };
    const penetration = minDist - dist;
    const contactPoint: Vector2D = {
      x: a.position.x + normal.x * a.radius,
      y: a.position.y + normal.y * a.radius,
    };

    return { normal, penetration, contactPoint };
  }

  testCircleSegment(circle: PhysicsBody, segment: SegmentShape): CollisionResult | null {
    const { start, end } = segment;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lenSq = dx * dx + dy * dy;

    if (lenSq === 0) {
      // Degenerate segment (point)
      const distX = circle.position.x - start.x;
      const distY = circle.position.y - start.y;
      const dist = Math.sqrt(distX * distX + distY * distY);
      if (dist >= circle.radius) return null;
      const normal: Vector2D = dist > 0
        ? { x: distX / dist, y: distY / dist }
        : { x: 0, y: -1 };
      return {
        normal,
        penetration: circle.radius - dist,
        contactPoint: { x: start.x, y: start.y },
      };
    }

    // Project circle center onto segment
    let t = ((circle.position.x - start.x) * dx + (circle.position.y - start.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));

    const closestX = start.x + t * dx;
    const closestY = start.y + t * dy;

    const distX = circle.position.x - closestX;
    const distY = circle.position.y - closestY;
    const distSq = distX * distX + distY * distY;

    if (distSq >= circle.radius * circle.radius) return null;

    const dist = Math.sqrt(distSq);
    const normal: Vector2D = dist > 0
      ? { x: distX / dist, y: distY / dist }
      : { x: -dy / Math.sqrt(lenSq), y: dx / Math.sqrt(lenSq) };

    return {
      normal,
      penetration: circle.radius - dist,
      contactPoint: { x: closestX, y: closestY },
    };
  }

  testCircleArc(circle: PhysicsBody, arc: ArcShape): CollisionResult | null {
    const dx = circle.position.x - arc.center.x;
    const dy = circle.position.y - arc.center.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Check if the circle center's angle is within the arc range
    let angle = Math.atan2(dy, dx);
    if (angle < 0) angle += Math.PI * 2;

    let startAngle = arc.startAngle;
    let endAngle = arc.endAngle;
    if (startAngle < 0) startAngle += Math.PI * 2;
    if (endAngle < 0) endAngle += Math.PI * 2;

    let inArc: boolean;
    if (startAngle <= endAngle) {
      inArc = angle >= startAngle && angle <= endAngle;
    } else {
      inArc = angle >= startAngle || angle <= endAngle;
    }

    if (!inArc) return null;

    // Check distance to arc
    const penetration = circle.radius - Math.abs(dist - arc.radius);
    if (penetration <= 0) return null;

    const normal: Vector2D = dist > 0
      ? { x: dx / dist, y: dy / dist }
      : { x: 0, y: -1 };

    // If inside the arc circle, flip normal
    if (dist < arc.radius) {
      normal.x = -normal.x;
      normal.y = -normal.y;
    }

    const contactPoint: Vector2D = {
      x: arc.center.x + (dx / dist) * arc.radius,
      y: arc.center.y + (dy / dist) * arc.radius,
    };

    return { normal, penetration, contactPoint };
  }

  testSweptCircleSegment(
    circle: PhysicsBody,
    segment: SegmentShape,
    dt: number
  ): CollisionResult | null {
    // Test at current position first
    const result = this.testCircleSegment(circle, segment);
    if (result) return result;

    // Test at predicted position
    const predicted: PhysicsBody = {
      ...circle,
      position: {
        x: circle.position.x + circle.velocity.x * dt,
        y: circle.position.y + circle.velocity.y * dt,
      },
    };
    return this.testCircleSegment(predicted, segment);
  }

  testSweptCircleFlipper(
    circle: PhysicsBody,
    flipper: FlipperBody,
    dt: number
  ): CollisionResult | null {
    // Model flipper as a segment from pivot to tip
    const tipX = flipper.pivot.x + Math.cos(flipper.currentAngle) * flipper.length;
    const tipY = flipper.pivot.y + Math.sin(flipper.currentAngle) * flipper.length;

    const segment: SegmentShape = {
      start: flipper.pivot,
      end: { x: tipX, y: tipY },
    };

    // Test current position
    const result = this.testCircleSegment(circle, segment);
    if (result) return result;

    // Test at predicted position with predicted flipper angle
    const predictedAngle = flipper.currentAngle + flipper.angularVelocity * dt;
    const predictedTipX = flipper.pivot.x + Math.cos(predictedAngle) * flipper.length;
    const predictedTipY = flipper.pivot.y + Math.sin(predictedAngle) * flipper.length;

    const predictedSegment: SegmentShape = {
      start: flipper.pivot,
      end: { x: predictedTipX, y: predictedTipY },
    };

    const predicted: PhysicsBody = {
      ...circle,
      position: {
        x: circle.position.x + circle.velocity.x * dt,
        y: circle.position.y + circle.velocity.y * dt,
      },
    };

    return this.testCircleSegment(predicted, predictedSegment);
  }
}
